import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { AnimatedViews, theme, useResponsive } from '@sg/garnish';
import {
  ProductDetails,
  useProductDetailsNavigationHeaderHeight,
} from '@sg/shared-fe-features-product-details';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsLoading = () => {
  const { currentBreakpoint } = useResponsive();

  if (currentBreakpoint.isXS) {
    return <ProductDetailsLoadingXS />;
  }

  return <ProductDetailsLoadingSM />;
};

// ─── Components ──────────────────────────────────────────────────────────────

const ProductDetailsLoadingXS = () => {
  const navigationHeaderHeight = useProductDetailsNavigationHeaderHeight();

  // ─── Styles ────────────────────────────────────────────────────────────────

  const headerDynamicStyle = useStyle(
    () => ({ paddingTop: navigationHeaderHeight }),
    [navigationHeaderHeight],
  );
  const headerStyles = [styles.headerXS, headerDynamicStyle];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <AnimatedViews.Pulse style={headerStyles}>
        <View style={styles.headerContentXS}>
          <View style={styles.titleXS} />
          <View style={styles.textLineFull} />
          <View style={styles.textLineShort} />
        </View>

        <View style={styles.productImageXS} />
      </AnimatedViews.Pulse>

      <ProductDetails.ContentBlock withBorder>
        <AnimatedViews.Pulse index={1} style={styles.sectionTitle} />

        <IngredientsPlaceholder />
      </ProductDetails.ContentBlock>
    </View>
  );
};

const ProductDetailsLoadingSM = () => {
  const { match } = useResponsive();

  const productImageSize = match([300, 340, 480, 580]);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const productImageDynamicStyle = useStyle(
    () => ({ width: productImageSize, height: productImageSize }),
    [productImageSize],
  );
  const productImageStyles = [styles.productImageSM, productImageDynamicStyle];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <ProductDetails.Container>
        <ProductDetails.Column withBorder centered>
          <AnimatedViews.Pulse style={productImageStyles} />
        </ProductDetails.Column>

        <ProductDetails.Column>
          <ProductDetails.ContentBlock withBorder>
            <AnimatedViews.Pulse index={1} style={styles.productSummarySM}>
              <View style={styles.titleSM} />

              <View>
                <View style={styles.textLineFull} />
                <View style={styles.textLineLong} />
                <View style={styles.textLineShort} />
              </View>

              <View style={styles.costAndNutrition} />
              <View style={styles.addToBagButton} />
            </AnimatedViews.Pulse>
          </ProductDetails.ContentBlock>

          <ProductDetails.ContentBlock>
            <AnimatedViews.Pulse index={2} style={styles.tabs}>
              <View style={styles.activeTab} />
              <View style={styles.tab} />
            </AnimatedViews.Pulse>

            <IngredientsPlaceholder />
          </ProductDetails.ContentBlock>
        </ProductDetails.Column>
      </ProductDetails.Container>
    </View>
  );
};

const IngredientsPlaceholder = () => {
  const ingredientIndexes = [...Array(PLACEHOLDER_INGREDIENTS).keys()];

  return (
    <View style={styles.ingredients}>
      {ingredientIndexes.map((index) => (
        <AnimatedViews.Pulse key={index} index={index + 3} style={styles.ingredient}>
          <View style={styles.ingredientImage} />

          <View style={styles.ingredientDetails}>
            <View style={styles.ingredientTitle} />
            <View style={styles.ingredientHighlight} />
            <View style={styles.ingredientHighlightShort} />
          </View>
        </AnimatedViews.Pulse>
      ))}
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PLACEHOLDER_INGREDIENTS = 3;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: 'hidden',
    backgroundColor: theme.colors.CREAM,
  },
  headerXS: {
    flexDirection: 'row',
    columnGap: theme.spacing['1'],
    paddingHorizontal: theme.spacing['6'],
    paddingBottom: theme.spacing['6'],
  },
  headerContentXS: {
    flex: 1,
    rowGap: theme.spacing['2'],
    paddingTop: theme.spacing['8'],
  },
  titleXS: {
    width: '84%',
    height: 32,
    marginTop: 4,
    marginBottom: theme.spacing['4'],
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  productImageXS: {
    width: 132,
    height: 132,
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  sectionTitle: {
    width: 180,
    height: 20,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  productImageSM: {
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  productSummarySM: {
    rowGap: theme.spacing['6'],
  },
  titleSM: {
    width: '64%',
    height: 44,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  textLineFull: {
    width: '100%',
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  textLineLong: {
    width: '92%',
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  textLineShort: {
    width: '56%',
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  costAndNutrition: {
    width: '48%',
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  addToBagButton: {
    width: 180,
    height: 56,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  tabs: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing['6'],
  },
  activeTab: {
    width: 160,
    height: 16,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  tab: {
    width: 120,
    height: 16,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  ingredients: {
    rowGap: theme.spacing['6'],
  },
  ingredient: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing['4'],
  },
  ingredientImage: {
    width: 133,
    height: 133,
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  ingredientDetails: {
    flexShrink: 1,
    width: 200,
    rowGap: theme.spacing['3'],
  },
  ingredientTitle: {
    width: '72%',
    height: 18,
    marginBottom: theme.spacing['1'],
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  ingredientHighlight: {
    width: '88%',
    height: 24,
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  ingredientHighlightShort: {
    width: '64%',
    height: 24,
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
});
